import { defineStore } from 'pinia';
import { useAuthUserStore } from '../user';
import { isUserAuthorized } from '@/app/stores/user/types';
import {
  findBudgetPaymentDataByPaymentIdAndUser,
  findConversionDataByPaymentIdAndUser,
  findCardTransferDataByPaymentIdAndUser,
  findSwiftDataByPaymentIdAndUser,
  findTransferDataByPaymentIdAndUser,
  findTransferByInvoiceDetailByIdAndUser,
  findRefundedQrOperationInfoByUser,
  getInfoPaymentSignerInfoByPaymentId,
  isPaymentConversionType,
  OperationHistory,
  isPaymentQrReturnedType,
  findQrTransferDataByPaymentId,
} from '@/shared/services/payment';
import { Operations } from '@/app/graphql/payment/sdk.payment';
import { DrawerPaymentParam, PaymentState } from './types';
import { useHistoryTransactionStore } from '@/app/stores/history-transaction';

export const usePaymentStore = defineStore('payment', {
  state: (): PaymentState => ({
    payment: null,
    signatories: [],
    errorHistory: false,
    isPaymentChanges: false,
    transactionLoading: false,
    transactionError: false,
    isShowSignDialog: false,
    dataPaymentForSign: [],
    dataSignPayment: null,
  }),
  getters: {
    getPaymentInfo: (state) => {
      return {
        legalPartyId: state.payment?.legalPartyId,
        id: state.payment?.id,
        transferNum: state.payment?.transferNum,
        payerAccount: state.payment?.payerAccount,
        recipientAccount: state.payment?.recipientAccount,
        amount: state.payment?.amount,
        purpose: !isPaymentConversionType(state.payment) ? state.payment?.purpose : '',
        currency: state.payment?.currencyCode,
        operationCode: state.payment?.operationCode,
        valueDate: state.payment?.valueDate,
        origin: !isPaymentConversionType(state.payment) ? state.payment?.origin : null,
        recipientDetail: isPaymentQrReturnedType(state.payment)
          ? state.payment?.cardTransferRko2CardRecord?.recipientDetail
          : '',
      } as OperationHistory;
    },
  },
  actions: {
    reset() {
      this.$reset();
    },
    clearPaymentsDetailInfo() {
      this.signatories = [];
      this.payment = null;
      this.isPaymentChanges = false;
    },
    setPaymentChanges(value: boolean) {
      this.isPaymentChanges = value;
    },
    async getPayment(payload: DrawerPaymentParam) {
      const { user } = useAuthUserStore();
      if (!isUserAuthorized(user)) return;
      const { getKeyAvailability, getTransactionTimeRule } = useHistoryTransactionStore();
      await getTransactionTimeRule();
      await getKeyAvailability();

      const findPayment = (operationCode: Operations) => {
        if (operationCode === Operations.Conversion) return findConversionDataByPaymentIdAndUser;
        if (operationCode === Operations.Swift) return findSwiftDataByPaymentIdAndUser;
        if (
          [
            Operations.Rko_2BusinessCardOwn,
            Operations.Rko_2BusinessCardForeign,
            Operations.Rko_2IndividualCardOwn,
            Operations.Rko_2IndividualCardForeign,
            Operations.BusinessCard_2RkoOwn,
            Operations.BusinessCard_2RkoForeign,
          ].includes(operationCode)
        )
          return findCardTransferDataByPaymentIdAndUser;
        if (operationCode === Operations.BudgetPayment) return findBudgetPaymentDataByPaymentIdAndUser;
        if (operationCode === Operations.Refund) return findRefundedQrOperationInfoByUser;
        if (operationCode === Operations.Qr) return findQrTransferDataByPaymentId;
        if (operationCode === Operations.TransferByInvoice) return findTransferByInvoiceDetailByIdAndUser;
        return findTransferDataByPaymentIdAndUser;
      };

      const { transactionTimeRule, transactionKeyAvailability } = useHistoryTransactionStore();
      if (!transactionTimeRule) return;

      try {
        const res = await findPayment(payload.operationCode)(
          payload.id,
          user,
          transactionTimeRule[payload.operationCode],
          transactionKeyAvailability
        );
        if (!res) return;
        this.payment = res;
      } catch (e: any) {
        this.transactionError = true;
      } finally {
        this.transactionLoading = false;
      }
    },
    async getPaymentSignerInfoByPaymentId(paymentId: number) {
      try {
        this.signatories = await getInfoPaymentSignerInfoByPaymentId(paymentId);
      } catch (e: any) {
        this.transactionLoading = false;
        this.transactionError = true;
      }
    },
    async getPaymentDetailInfo(payload: DrawerPaymentParam) {
      this.clearPaymentsDetailInfo();
      this.transactionLoading = true;
      this.transactionError = false;
      await this.getPaymentSignerInfoByPaymentId(payload.id);
      await this.getPayment(payload);
    },
  },
});
